import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useFormat } from "../../i18n/format";
import type { Translate } from "../../i18n/translate";
import {
  formatMoneyTotals,
  moneyIsEmpty,
  revenueNote,
} from "./offerRevenueFormat";
import type { OfferFunnelRow } from "@shared/types/offerFunnel";

/**
 * One offer's funnel, with the DROP at each step as the thing you read first.
 *
 * ```
 * impressions  →  engaged (≥5s)  →  opened  →  requested  →  accepted  →  revenue
 * └──────────── the television counted ──────┘  └──── reception accepted ────┘
 * ```
 *
 * ## Why the five counts share a scale and revenue does not
 *
 * Every bar here is a share of the same denominator — impressions — so the
 * collapse from four thousand shows to two requests is visible as geometry
 * rather than as arithmetic the reader has to do. Revenue is money, not a
 * count, and putting it on that axis would be a second y-scale wearing the
 * first one's clothes. So it ends the strip as a figure, not a bar. An offer
 * with 4 000 impressions and 2 requests is telling the hotel to change the
 * picture or the price, and that sentence has to be legible in one glance.
 *
 * ## The line down the middle is doctrine, not decoration
 *
 * The first three stages are counts from `engagement_daily` — per premise, per
 * UTC day, no room, no price, no request id. The last three are rows in
 * `guest_requests` — individual, priced, per room. They are compared over a
 * window and **never joined per guest**: no impression can be traced to a
 * request (`docs/room-offers.md` §5, and POS-11's own types). The rule drawn
 * between "opened" and "requested" is where that changes, and the two series
 * colours carry the same fact so a screenshot keeps it.
 *
 * A tiny bar is not a rendering bug. A stage that rounds to nothing still gets
 * a visible sliver and always gets its number printed beside it, because the
 * number is the evidence and the bar is only the shape of it.
 */

/** Bars this thin read as data rather than as furniture. */
const BAR_H = 10;
/** A stage that is a rounding error still has to be visible. */
const MIN_BAR_PCT = 0.8;

interface Stage {
  key: string;
  label: string;
  value: number;
  /** Which store the number came from — decides its colour and its claim. */
  half: "attention" | "transaction";
  hint?: string;
}

export function funnelStages(t: Translate, row: OfferFunnelRow): Stage[] {
  return [
    {
      key: "impressions",
      label: t("offers:offerFunnelStrip.stages.impressions"),
      value: row.impressions,
      half: "attention",
      hint: t("offers:offerFunnelStrip.stages.impressionsHint"),
    },
    {
      key: "engaged",
      label: t("offers:offerFunnelStrip.stages.engaged"),
      value: row.engaged,
      half: "attention",
      hint: revenueNote(t, "engagedProxy"),
    },
    {
      key: "opens",
      label: t("offers:offerFunnelStrip.stages.opens"),
      value: row.opens,
      half: "attention",
      hint: t("offers:offerFunnelStrip.stages.opensHint"),
    },
    {
      key: "requests",
      label: t("offers:offerFunnelStrip.stages.requests"),
      value: row.requests,
      half: "transaction",
      hint: t("offers:offerFunnelStrip.stages.requestsHint"),
    },
    {
      key: "accepted",
      label: t("offers:offerFunnelStrip.stages.accepted"),
      value: row.accepted,
      half: "transaction",
      hint: t("offers:offerFunnelStrip.stages.acceptedHint"),
    },
  ];
}

/**
 * The drop from one stage to the next, as a percentage of the stage above.
 *
 * Null for the first stage (nothing to fall from) and for a stage whose parent
 * is zero — "-100%" of nothing is a claim, and there is nothing to claim.
 *
 * **A stage can legitimately be BIGGER than the one above it**, and the number
 * that comes back is then positive. `engaged` only counts a view that held the
 * screen for `SLOT_ENGAGED_SECONDS`, while `opens` has no dwell requirement at
 * all — a guest who taps into the detail card after two seconds produces an
 * open with no engagement behind it. So 10 impressions / 3 engaged / 5 opens is
 * ordinary data, not corrupt data, and the caller must render a rise as a rise
 * rather than printing "−-67%" on the one page whose whole job is looking
 * credible.
 */
export function dropFrom(previous: number, current: number): number | null {
  if (previous <= 0) return null;
  return (previous - current) / previous;
}

/** `−67%`, `+40%`, or nothing at all. Never a doubled sign. */
export function formatDrop(drop: number | null): string {
  if (drop === null) return "";
  const pct = Math.round(drop * 100);
  if (pct === 0) return "0%";
  return pct > 0 ? `−${pct}%` : `+${Math.abs(pct)}%`;
}

export default function OfferFunnelStrip({ row }: { row: OfferFunnelRow }) {
  const { t } = useTranslation("offers");
  const fmt = useFormat();
  const stages = funnelStages(t, row);
  const max = stages[0].value;

  return (
    <Stack spacing={0.75} data-testid={`funnel-${row.offerId}`}>
      {stages.map((stage, i) => {
        const previous = i === 0 ? null : stages[i - 1].value;
        const drop = previous === null ? null : dropFrom(previous, stage.value);
        const pct = max > 0 ? (stage.value / max) * 100 : 0;
        const width = stage.value > 0 ? Math.max(pct, MIN_BAR_PCT) : 0;
        const colour =
          stage.half === "attention" ? tokens.vizAttention : tokens.vizMoney;
        return (
          <Box key={stage.key}>
            {/* Where the grain changes. Everything above it is a count with no
                room attached; everything below is a priced row. */}
            {stage.key === "requests" && (
              <Box
                sx={{
                  borderTop: `1px solid ${tokens.divider}`,
                  mt: 1.25,
                  mb: 1.25,
                }}
              />
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "130px 1fr 96px 74px",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Tooltip title={stage.hint ?? ""} placement="top-start">
                <Typography
                  variant="meta"
                  sx={{ color: tokens.inkMuted }}
                  noWrap
                >
                  {stage.label}
                </Typography>
              </Tooltip>
              <Box
                sx={{
                  height: BAR_H,
                  bgcolor: tokens.rowAlt,
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <Box
                  data-testid={`funnel-bar-${row.offerId}-${stage.key}`}
                  data-share={pct.toFixed(2)}
                  sx={{
                    height: "100%",
                    width: `${width}%`,
                    bgcolor: colour,
                    // Square where it grows from, rounded where the data ends.
                    borderRadius: "0 4px 4px 0",
                  }}
                />
              </Box>
              <Typography
                variant="bodySm"
                sx={{
                  color: tokens.ink,
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "right",
                }}
              >
                {fmt.number(stage.value)}
              </Typography>
              <Typography
                variant="meta"
                sx={{
                  color: tokens.inkFaint,
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "right",
                }}
                data-testid={`funnel-drop-${row.offerId}-${stage.key}`}
              >
                {formatDrop(drop)}
              </Typography>
            </Box>
          </Box>
        );
      })}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "130px 1fr",
          alignItems: "center",
          gap: 1,
          mt: 0.5,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              bgcolor: tokens.vizMoney,
            }}
          />
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {t("offerFunnelStrip.revenue")}
          </Typography>
        </Stack>
        <Typography
          sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
          data-testid={`funnel-revenue-${row.offerId}`}
        >
          {moneyIsEmpty(row.revenue) ? (
            <Box component="span" sx={{ fontWeight: "fontWeightMedium" }}>
              {t("offerFunnelStrip.nothingPriced")}
            </Box>
          ) : (
            formatMoneyTotals(fmt, row.revenue)
          )}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * The legend for the two halves. Mounted once per card rather than per strip:
 * one legend read once beats the same two swatches repeated down a page.
 */
export function FunnelLegend() {
  const { t } = useTranslation("offers");
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      <LegendKey
        colour={tokens.vizAttention}
        label={t("offerFunnelStrip.legend.televisions")}
      />
      <LegendKey
        colour={tokens.vizMoney}
        label={t("offerFunnelStrip.legend.reception")}
      />
    </Stack>
  );
}

function LegendKey({ colour, label }: { colour: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: colour }}
      />
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Stack>
  );
}
